import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import 'bpmn-js/dist/assets/diagram-js.css';
import Modeler from 'bpmn-js/lib/Modeler';

import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

import { Box, Button, Group, Modal, Tabs, Textarea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { convertSvgToPng } from '../../../utils/ImageUtils.tsx';
import { useModal } from '../../../utils/useModal.tsx';

export interface BpmnGeneratorModalRef {
    openModal: () => void;
}

interface BpmnGeneratorModalProps {
    onInsert: (image: string) => void;
}

const initialXml = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
                  xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
                  xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
                  id="Definitions_1"
                  targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="Process_1" isExecutable="false">
    <bpmn:startEvent id="StartEvent_1"/>
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="StartEvent_1_di" bpmnElement="StartEvent_1">
        <dc:Bounds x="150" y="150" width="36" height="36"/>
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

const BpmnGeneratorModal = forwardRef<
    BpmnGeneratorModalRef,
    BpmnGeneratorModalProps
>(({ onInsert }, ref) => {
    const [opened, { open, close }] = useDisclosure();
    const [xml, setXml] = useState<string>(initialXml);
    const [activeTab, setActiveTab] = useState<string | null>('editor');
    const { showConfirmWarningModal } = useModal();

    const containerRef = useRef<HTMLDivElement | null>(null);
    const modelerRef = useRef<Modeler | null>(null);

    useImperativeHandle(ref, () => ({
        openModal: open
    }));

    useEffect(() => {
        const initializeModeler = async () => {
            if (containerRef.current && !modelerRef.current) {
                const modeler = new Modeler({
                    container: containerRef.current
                });

                try {
                    await modeler.importXML(xml);
                    modelerRef.current = modeler;
                } catch (err) {
                    console.error('Error importing BPMN diagram', err);
                }
            }
        };

        if (opened) {
            requestAnimationFrame(() => {
                void initializeModeler();
            });
        } else {
            modelerRef.current?.destroy();
            modelerRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened]);

    const handleTabChange = async (value: string | null) => {
        if (!modelerRef.current) return;

        if (value === 'xml') {
            // Export the current model to XML when switching to the XML tab
            try {
                const { xml: currentXml } = await modelerRef.current.saveXML({
                    format: true
                });
                setXml(currentXml ?? initialXml);
            } catch (err) {
                console.error('Failed to export XML', err);
            }
        }

        if (value === 'editor') {
            // Import the edited XML back into the modeler
            try {
                await modelerRef.current.importXML(xml);
            } catch (err) {
                console.error('Failed to re-import XML into modeler', err);
            }
        }

        setActiveTab(value);
    };

    const handleClose = () => {
        showConfirmWarningModal(
            'Are you sure?',
            'Are you sure you want to close the BPMN Generator, the current diagram will not be saved.',
            () => {
                close();
            }
        );
    };

    const handleInsert = async () => {
        if (!modelerRef.current) return;

        try {
            const { svg } = await modelerRef.current.saveSVG();
            const png = await convertSvgToPng(svg);
            onInsert?.(png);
            close();
        } catch (err) {
            console.error('Failed to export BPMN diagram as image', err);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            closeOnClickOutside={false}
            closeOnEscape={false}
            title='Generate BPMN Diagram'
            size='xl'
        >
            <Tabs value={activeTab} onChange={handleTabChange}>
                <Tabs.List>
                    <Tabs.Tab value='editor'>Editor</Tabs.Tab>
                    <Tabs.Tab value='xml'>XML</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value='editor' pt='sm'>
                    <Box
                        ref={containerRef}
                        style={{
                            background: 'white',
                            width: '100%',
                            height: '500px',
                            border: '1px solid #ccc'
                        }}
                    />
                </Tabs.Panel>

                <Tabs.Panel value='xml' pt='md'>
                    <Textarea
                        minRows={22}
                        maxRows={22}
                        autosize
                        value={xml}
                        onChange={e => setXml(e.currentTarget.value)}
                    />
                </Tabs.Panel>
            </Tabs>
            <Group mt='lg' justify='flex-end'>
                <Button onClick={handleClose} variant='outline'>
                    Cancel
                </Button>
                <Button variant='filled' onClick={handleInsert}>
                    Insert Diagram
                </Button>
            </Group>
        </Modal>
    );
});

// component factory forwardRef hides display name
BpmnGeneratorModal.displayName = 'BpmnGeneratorModal';
export default BpmnGeneratorModal;
